<template>
  <nav
    ref="slidesContainer"
    class="flex flex-col bg-gray-900 text-white border-r border-gray-700 overflow-y-auto select-none thumbnail-variant"
    data-testid="deck-sidebar"
  >
    <div class="flex flex-col min-h-max w-full h-full overflow-x-clip p-4 relative">
      <div class="mb-10">
        <a
          ref="companyLogo"
          class="mx-auto mb-2 max-w-full cursor-pointer text-sm"
          :href="companyPageUrl"
          target="_blank"
          data-testid="deck-slides-company-logo"
        >
          <img
            v-if="deck.company.logo"
            class="block mb-2 max-h-[60px] w-auto rounded p-1 bg-white"
            :src="deck.company.logo"
            :alt="deck.company.name"
            draggable="false"
          />
          <span>{{ deck.company.name }}</span>
        </a>

        <VTooltip direction="top" class="w-full [&>.vtooltip-positioner]_w-full">
          <template #content>
            {{ deck.title }}
          </template>
          <h4 class="text-ellipsis-2 mb-0 normal-case text-base tracking-normal line-clamp-2">{{ deck.title }}</h4>
        </VTooltip>
      </div>
      <div ref="slidesContainer" class="flex flex-1 flex-col justify-between relative">
        <ol class="mb-0">
          <DeckTile
            v-for="(slide, index) in deck.slides"
            :key="slide.id"
            :slide="slide"
            :index="index"
            :slide-number="getSlideNumber(index)"
            :selected="index === currentSlideIndex"
            :disabled="slide.disabled"
            tile-type="thumbnail"
            @on-slide-selected="handleSlideSelect"
          />
        </ol>
      </div>
      <div v-if="showAutoplay" class="mt-6">
        <VButton variant="dark-gray-borderless w-full hover_!bg-gray-700" size="sm" rounded @click="$emit('start-auto-play')">
          <span class="text-blue-200"><PlayIcon class="inline -mb-0.5 h-4 w-4 mr-2 align-baseline" />Autoplay</span>
        </VButton>
      </div>
      <div
        v-if="isBookAMeetingEnabled && !user.deckUserType"
        class="flex flex-col justify-between mt-6 pt-4 border-t border-[#5D5C5C] text-blue-200"
      >
        <VButton
          v-if="isBookAMeetingEnabled"
          v-analytics="'bookAMeetingClicked'"
          class="text-center mt-3"
          data-testid="deck-slides-bam-btn"
          component="a"
          variant="flat"
          rounded
          size="sm"
          :href="`/meetings/book/${deck.request_setting_id}`"
          target="_blank"
          analytics-event-name="BookAMeeting"
          location="deck-sidebar"
        >
          <CalendarDaysIcon class="mr-2 w-4 h-4 inline" />Book a Meeting</VButton
        >
      </div>
      <div
        v-if="!nonShareableMeeting && shareCode"
        class="text-center bg-[#303030] text-white -mx-4 -mb-4 py-6 px-4 rounded-t-lg sticky bottom-0"
      >
        <p class="select-text text-sm mb-4" data-testid="slides-list-share-code">
          Meeting Code: <span class="font-bold">{{ formatShareCode(shareCode) }}</span>
        </p>
        <VCopyButton class="w-full flex justify-center items-center" variant="dark-gray-borderless" size="sm" :copy-text="copyText" rounded>
          <span class="text-blue-200"><ArrowUpOnSquareIcon class="inline h-5 w-5 -mt-1 mr-2" />Share Join Info</span>
        </VCopyButton>
      </div>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { type Deck, type DeckUser, type IncludeCompany } from '@/entities/deck'
import { PlayIcon, ArrowUpOnSquareIcon, CalendarDaysIcon } from '@heroicons/vue/24/outline'
import type { DeckWithEmbed } from '@/entities/embed'
import type { Meeting } from '@/entities/meeting'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  meeting: {
    type: Object as PropType<DeepReadonly<Meeting>>,
    default: undefined,
  },
  currentSlideIndex: {
    type: Number,
    required: true,
  },
  analyticsEventName: {
    type: String,
    required: true,
  },
  uuid: {
    type: String,
    default: null,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  shareCode: {
    type: String,
    default: null,
  },
  nonShareableMeeting: {
    type: Boolean,
    default: false,
  },
  isSidebarOpen: {
    type: Boolean,
    required: true,
  },
  embedDeck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed>>,
    default: null,
  },
  showAutoplay: {
    type: Boolean,
    default: () => false,
  },
})

const slidesContainer = ref<HTMLElement | null>(null)

const emit = defineEmits(['set-current-slide-index', 'start-auto-play'])
const { $analytics } = useNuxtApp()

const companyLogo: Ref<HTMLElement | null> = ref(null)

const { copyText } = useShareMeetingLink({
  shareCode: toRef(props, 'shareCode'),
  user: toRef(props, 'user'),
  conference: toRef(props, 'meeting'),
})

const isBookAMeetingEnabled = computed(() => {
  const { show_bam_sidebar_button, request_setting } = props.deck
  return !!(show_bam_sidebar_button && request_setting?.is_active)
})

watch(() => props.currentSlideIndex, _scrollSelectedSlideIntoView)
watch(
  () => props.isSidebarOpen,
  (isSidebarOpen: boolean) => isSidebarOpen && _scrollSelectedSlideIntoView(),
)

onMounted(_scrollSelectedSlideIntoView)

function handleSlideSelect(index: number) {
  $analytics.track(`${props.analyticsEventName}SlideSelected`, {
    slideIndex: index,
    deckId: props.deck.id,
    deckName: props.deck.title,
    companyId: props.deck.company.id,
    companyName: props.deck.company.name,
    slideId: props.deck.slides[index].id,
    uuid: props.uuid,
    partnerId: props.embedDeck?.partner_id,
    partnerName: props.embedDeck?.partner_name,
    embedLocation: props.embedDeck?.embed_name,
  })

  emit('set-current-slide-index', index)
}

function formatShareCode(code: string): string {
  if (code.length <= 5) return code

  return code.match(/.{4}(?=.{2,4})|.+/g)?.join('-') ?? ''
}

function _scrollSelectedSlideIntoView() {
  if (!slidesContainer.value) return

  const index = props.currentSlideIndex
  const selected = slidesContainer.value.querySelectorAll('li')[index]

  if (!selected) return

  requestAnimationFrame(() => {
    slidesContainer.value?.scrollTo({ top: selected.offsetTop - selected.clientHeight / 2, behavior: 'smooth' })
  })
}
const companyPageUrl = computed(() => `/companies/${props.deck?.company?.id}`)

function getSlideNumber(index: number = 0): number {
  let number = 0
  for (let i = 0; i <= index; i++) {
    if (props.deck?.slides[index].type !== 'H') {
      number++
    }
  }
  return number
}
</script>

<style lang="stylus" scoped>
.slides-container
  -webkit-overflow-scrolling: touch
</style>
